/**
 * 图片压缩工具
 * 使用 Canvas 对图片进行压缩，支持配置最大宽高、质量、输出格式等
 */

const DEFAULT_OPTIONS = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  mimeType: 'image/jpeg',
}

/**
 * 将 File 转换为 HTMLImageElement
 */
function fileToImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('图片加载失败'))
    }
    img.src = url
  })
}

/**
 * 计算压缩后的尺寸，保持宽高比
 */
function calcSize(width, height, maxWidth, maxHeight) {
  if (width <= maxWidth && height <= maxHeight) {
    return { width, height }
  }
  const ratio = Math.min(maxWidth / width, maxHeight / height)
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  }
}

/**
 * 通过 Canvas 压缩图片
 */
function compressWithCanvas(img, options) {
  return new Promise((resolve) => {
    const { width, height } = calcSize(img.width, img.height, options.maxWidth, options.maxHeight)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0, width, height)
    canvas.toBlob(
      (blob) => resolve(blob),
      options.mimeType,
      options.quality,
    )
  })
}

/**
 * 压缩图片文件
 * @param {File} file - 原始图片文件
 * @param {Object} options - 压缩选项
 * @param {number} [options.maxWidth=1920] - 最大宽度
 * @param {number} [options.maxHeight=1920] - 最大高度
 * @param {number} [options.quality=0.8] - 压缩质量 0-1
 * @param {string} [options.mimeType='image/jpeg'] - 输出格式
 * @returns {Promise<File>} 压缩后的文件
 */
export async function compressImage(file, options = {}) {
  // 非图片类型直接返回
  if (!file.type.startsWith('image/')) {
    return file
  }
  // GIF/SVG 不做压缩
  if (['image/gif', 'image/svg+xml'].includes(file.type)) {
    return file
  }

  const opts = { ...DEFAULT_OPTIONS, ...options }
  // PNG 保持原格式以支持透明背景
  if (file.type === 'image/png') {
    opts.mimeType = 'image/png'
  }

  const img = await fileToImage(file)
  const blob = await compressWithCanvas(img, opts)

  // 如果压缩后反而更大，返回原文件
  if (blob.size >= file.size) {
    return file
  }

  const ext = opts.mimeType === 'image/png' ? '.png' : '.jpg'
  const fileName = file.name.replace(/\.[^.]+$/, ext)
  return new File([blob], fileName, { type: opts.mimeType, lastModified: Date.now() })
}
